/**
 * Secure (cookie-authenticated) asset binary delivery.
 *
 * Asset binaries are served from the assets CDN host — a different origin than the app.
 * so the browser does NOT send the session cookie (authtoken/__cs_user) with the binary request.
 * A private asset's binary URL is rewritten to a same-origin proxy path so the request stays
 * same-origin with the app and the cookie is attached automatically.
 *
 */

export const DEFAULT_SECURE_ASSET_DELIVERY_PREFIX = '/am-assets'

export interface SecureAssetDeliveryOptions {
  /** Whether the asset's resolved (effective) access is private. */
  isPrivate: boolean
  /**
   * Same-origin path that the Fastly/dev proxy routes to the assets origin.
   * Must match the Fastly VCL rule + dev-server proxy. Defaults to `/am-assets`.
   */
  prefix?: string
}

const isTransformableUrl = (url: string): boolean => url.includes('://') || url.startsWith('/')

/**
 * Rewrites a private asset's binary URL to the same-origin secure delivery path.
 * No-op for public assets, empty/non-http(s) URLs, and URLs already under the prefix.
 * Preserves the path and all query params (transforms, workspace, locale).
 */
export const buildSecureAssetDeliveryUrl = (
  url: string | undefined,
  options: SecureAssetDeliveryOptions
): string | undefined => {
  const prefix = (options?.prefix ?? DEFAULT_SECURE_ASSET_DELIVERY_PREFIX).replace(/\/+$/, '')

  if (!url || !options?.isPrivate || !prefix || !isTransformableUrl(url)) {
    return url
  }

  try {
    const assetUrl = new URL(url, window.location.origin)
    // Idempotent: don't double-prefix a URL already routed through the proxy.
    if (assetUrl.pathname === prefix || assetUrl.pathname.startsWith(`${prefix}/`)) {
      return url
    }
    return `${prefix}${assetUrl.pathname}${assetUrl.search}`
  } catch (error: any) {
    console.log(
      'Error on adding Pre-fix to Secure URL',
      JSON.stringify({ error: { message: error.message ?? '', stack: error.stack ?? '' } })
    )
    return url
  }
}

/** True when the AM2.0 asset's resolved effective access is private. */
export const isPrivateAssetAccess = (asset?: {
  settings?: { privacy_settings?: { asset_access?: string } }
}): boolean => asset?.settings?.privacy_settings?.asset_access === 'private'
